// /skillsDashboard/services/AllGraduatesAPI.js
import React from "react";
import {
  SchoolOutlined,
  TrendingUp,
  Female,
  Person,
  Public,
  Science,
  WorkOutline,
  CalendarToday,
  AccountBalance,
  Business,
  EmojiPeople,
  } from "@mui/icons-material";

const AllGraduatesAPI = {
  // ✅ Correct base URL to match your backend profile routes
  baseURL: "/api/profile/skills-profile",

  // ---------------------- Generic API Helper ----------------------
  async makeAPICall(endpoint, params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const url = `${this.baseURL}${endpoint}${queryString ? `?${queryString}` : ""}`;

      const response = await fetch(url, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error(`API call failed: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error fetching data from ${endpoint}:`, error);
      throw error;
    }
  },

  // ---------------------- Icon Utility ----------------------
  getIconComponent(iconType) {
    const iconMap = {
      school: <SchoolOutlined />,
      "trending-up": <TrendingUp />,
      female: <Female />,
      person: <Person />,
      public: <Public />,
      science: <Science />,
      work: <WorkOutline />,
      calendar: <CalendarToday />,
      "account-balance": <AccountBalance />,
      business: <Business />,
    };
    return iconMap[iconType] || <SchoolOutlined />;
  },

      // ---------------------- Parallel Data Fetch ----------------------
    async fetchDashboardData(filters = {}) {
  try {
    const [
  kpiGraduateData,
  educationLevelData,
  countryStudyData,
  employmentByFieldData,
  genderByFieldData,
  graduatesByGender,
  youthGraduatesByGender,
  genderEmploymentData,
  ageDistributionData,
  generalChartData,
] = await Promise.all([
  this.getKPIGraduateData(filters),
  this.getEducationLevelData(filters),
  this.getCountryStudyData(filters),
  this.getEmploymentByFieldData(filters),
  this.getGenderByFieldData(filters),
  this.getGraduatesByGender(filters),
  this.getYouthGraduatesByGender(filters),
  this.getGenderEmploymentData(filters),
  this.getAgeDistributionData(filters),
  this.getGeneralChartData(filters),
]);
return {
  kpiGraduateData,
  educationLevelData,
  countryStudyData,
  employmentByFieldData,
  genderByFieldData,
  graduatesByGender,
  youthGraduatesByGender,
  genderEmploymentData,
  ageDistributionData,
  generalChartData,
};

  } catch (error) {
    console.error("Error fetching All Graduates Dashboard data:", error);
    throw error;
  }
},

  // ---------------------- Individual Endpoints ----------------------

  async getKPIGraduateData(filters) {
    try {
      const data = await this.makeAPICall("/kpi-graduate", filters);
      return data.map((item) => ({
        title: item.title,
        value: item.value,
        change: item.change,
        trend: item.trend,
        icon: this.getIconComponent(item.iconType),
        color: item.color,
        description: item.description || "",  
      }));
    } catch (error) {
    console.error("Error fetching KPI Graduate data:", error);
    return [
      {
        title: "Total Graduates",
        value: "0",
        icon: <SchoolOutlined />,
        color: "#1976d2",
        description: "Total number of graduates registered in IPPIS", // ✅ Fallback example
      },
      {
        title: "Employment Rate",
        value: "0%",
        icon: <TrendingUp />,
        color: "#388e3c",
        description: "Share of graduates currently employed",
      },
      {
        title: "Female Graduates",
        value: "0%",
        icon: <Female />,
        color: "#f57c00",
        description: "Percentage of graduates who are female",
      },

       {
        title: "Youth Graduates",
        value: "0%",
        icon: <EmojiPeople  />,
        color: "#6a7c72ff",
        description: "Percentage of graduates who are 30 and below years old",
      },

        {
        title: "Studied Abroad",
        value: "0%",
        icon: <Public />,
        color: "#00a3f5ff",
        description: "Percentage of graduates who graduated from Abroud Schools",
      },

      {
        title: "Top Field",
        value: "0%",
        icon: <TrendingUp />,
        color: "#00a3f5ff",
        description: "Field of Study with the highest number of graduates",
      },
    ];
  }
},


  async getEducationLevelData(filters) {
  try {
    return await this.makeAPICall("/all_education-level", filters);
  } catch (error) {
    console.error("Error fetching Education Level data:", error);
    return [];
  }
},

  async getCountryStudyData(filters) {
  try {
    return await this.makeAPICall("/all_country-study", filters);
  } catch (error) {
    console.error("Error fetching Country Study data:", error);
    return [];
  }
},

  async getEmploymentByFieldData(filters) {
  try {
    return await this.makeAPICall("/graduates-by-field", filters);
  } catch (error) {
    console.error("Error fetching Employment by Field data:", error);
    return [];
  }
},

async getGenderByFieldData(filters) {
  try {
    return await this.makeAPICall("/graduates-by-gender-and-field", filters);
  } catch (error) {
    console.error("Error fetching Gender by Field data:", error);
    return [];
  }
},

  async getGraduatesByGender(filters) {  // 
    try {
      return await this.makeAPICall("/graduates-by-gender", filters);
    } catch (error) {
      console.error("Error fetching Graduates by Gender data:", error);
      return [];
    }
  },

  async getYouthGraduatesByGender(filters) {
  try {
    return await this.makeAPICall("/youth-graduates-by-gender", filters);
  } catch (error) {
    console.error("Error fetching Youth Graduates by Gender data:", error);
    return [];
  }
},


  async getGenderEmploymentData(filters) {
    try {
      return await this.makeAPICall("/gender-employment", filters);
    } catch (error) {
      console.error("Error fetching Gender Employment data:", error);
      return [];
    }
  },

  async getAgeDistributionData(filters) {
    try {
      return await this.makeAPICall("/age-distribution-all-graduates", filters);
    } catch (error) {
      console.error("Error fetching Age Distribution data:", error);
      return [];
    }
  },

  async getGeneralChartData(filters) {
    try {
      return await this.makeAPICall("/general-charts", filters);
    } catch (error) {
      console.error("Error fetching General Chart data:", error);
      return [];
    }
  },
};

export default AllGraduatesAPI;
